/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import template from './folder-edit-page.component.html';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

interface FolderExtDatum {
  key: string;
  value: string;
}

@Component({
  standalone: false,
  selector: 'adn-folder-edit-page',
  template
})
export class FolderEditPageComponent extends AdminResourceEditPageBase {
  extData: FolderExtDatum[] = [];
  allTeams: any[] | null = null;
  teamsWarning = false;
  readonly canUpdateFolder = true;

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(TeamResource) private Team: any,
    private cdr: ChangeDetectorRef,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    this.deferFormDirtyTracking();

    this.Team.query().$promise.then((page: any) => {
      // null → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
      deferMarkForCheck(this.cdr, () => {
        this.allTeams = page.results;
        this.resumeFormDirtyTracking();
      });
    });
  }

  protected afterInitEditingModel(): void {
    this.editingModel.dataSource = this.editingModel.dataSource || 'ADNUNTIUS';
    this.extData = _.map(this.editingModel.data || {}, (value: string, key: string) => ({key, value}));
    this.editingModel.teams = this.normalizeTeamIds(this.editingModel.teams);
  }

  protected afterInitAfterSave(saveObj?: any): void {
    if (!saveObj) {
      return;
    }
    const savedTeams = _.get(saveObj, 'teams') || [];
    this.teamsWarning = (this.editingModel.teams || []).length !== savedTeams.length;
    this.editingModel.teams = this.normalizeTeamIds(savedTeams);
  }

  protected onBeforeSubmit(): void {
    this.editingModel.data = {};
    _.forEach(_.filter(this.extData, (datum) => {
      return _.trim(datum.value).length > 0 && _.trim(datum.key).length > 0;
    }), (datum) => {
      this.editingModel.data[datum.key] = datum.value;
    });
  }

  addData(): void {
    this.extData = [...this.extData, {key: '', value: ''}];
    this.markExternalFormDirty();
  }

  deleteData(index: number): void {
    this.extData = this.extData.filter((_datum, i) => i !== index);
    this.markExternalFormDirty();
  }

  onExtDataChange(): void {
    this.markExternalFormDirty();
  }

  selectTeams(): void {
    this.editingModel.teams = _.map(this.allTeams, 'id');
    this.markExternalFormDirty();
  }

  deselectTeams(): void {
    this.editingModel.teams = [];
    this.markExternalFormDirty();
  }

  isTeamSelected(teamId: string): boolean {
    return (this.editingModel.teams || []).indexOf(teamId) > -1;
  }

  toggleTeam(teamId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const teams = [...(this.editingModel.teams || [])];
    const index = teams.indexOf(teamId);

    if (checked && index < 0) {
      teams.push(teamId);
    } else if (!checked && index > -1) {
      teams.splice(index, 1);
    }

    this.editingModel.teams = teams;
    this.markExternalFormDirty();
  }

  trackByTeamId(_index: number, team: any): string {
    return team.id;
  }

  trackByExtDataIndex(index: number): number {
    return index;
  }

  private normalizeTeamIds(teams: any): string[] {
    return _.map(teams || [], (team: any) => _.isString(team) ? team : team.id);
  }
}
